export const SCHOOL_NAME = 'The Rising School';
export const TIMEFRAMES = [['daily', 'Daily (Today)'], ['weekly', 'Weekly (Last 7 Days)'], ['monthly', 'Monthly (Current Month)']];
export const STATUS_EMOJI = { Exemplary: '🟢', 'Needs Improvement': '🟡', 'Action Required': '🔴', Holiday: '🏖️' };

export const dateRangeLabel = (r) => (r.from === r.to ? r.from : `${r.from} to ${r.to}`);

/** Rows that are expected to submit (holiday classes excluded). */
export const activeRows = (report) => (report?.rows || []).filter((r) => !r.holiday);

const DEFAULT_SCOPE = { includeAttendance: true, includeDiary: true };

/**
 * WhatsApp text driven by the scope checkboxes (WhatsApp markdown: *bold*).
 *  A) attendance only  B) diary only  C) both (also used when neither is ticked).
 */
export function shareComplianceWhatsApp(report, scope = DEFAULT_SCOPE) {
  const rows = activeRows(report);
  const total = (report.summary && report.summary.totalActiveClasses) ?? rows.length;
  const daily = report.timeframe === 'daily';
  const aDone = (r) => (daily ? r.attendanceStatus === 'Submitted' : r.workingDays > 0 && r.attendanceDays === r.workingDays);
  const dDone = (r) => (daily ? r.diaryStatus === 'Submitted' : r.workingDays > 0 && r.diaryDays === r.workingDays);
  const aCount = rows.filter(aDone).length;
  const dCount = rows.filter(dDone).length;
  const onlyAtt = scope.includeAttendance && !scope.includeDiary;
  const onlyDiary = scope.includeDiary && !scope.includeAttendance;

  const attLine = (r) => (daily
    ? (aDone(r) ? `🟢 ${r.attendanceTime || 'Submitted'} (${r.punctuality})` : '🔴 Not Marked')
    : `${aDone(r) ? '🟢' : '🔴'} ${r.attendanceDays}/${r.workingDays} days (${r.punctualityRate}% on-time)`);
  const diaryLine = (r) => (daily
    ? (dDone(r) ? `🟢 Posted${r.diaryTime ? ` ${r.diaryTime}` : ''}` : '🔴 Not Posted')
    : `${dDone(r) ? '🟢' : '🔴'} ${r.diaryDays}/${r.workingDays} days`);

  const head = (title) => [`${title}`, `🏫 *${SCHOOL_NAME}*`, `📅 *Period:* ${report.label} (${dateRangeLabel(report)})`, '-----------------------------------'];
  let lines;
  if (onlyAtt) {
    lines = [...head('📋 *STAFF ATTENDANCE COMPLIANCE REPORT*'),
      `✅ *Attendance Marked:* ${aCount}/${total}`, `🔴 *Attendance Pending:* ${total - aCount}`, '',
      '📋 *Teacher-Wise Attendance Status:*',
      ...rows.map((r) => `• *${r.classId} (${r.teacherName}):* ${attLine(r)}`), '',
      '⚠️ *Teachers with pending attendance are requested to submit their class attendance immediately.*'];
  } else if (onlyDiary) {
    lines = [...head('📓 *STAFF DAILY DIARY COMPLIANCE REPORT*'),
      `✅ *Diary Submitted:* ${dCount}/${total}`, `🔴 *Diary Pending:* ${total - dCount}`, '',
      '📋 *Teacher-Wise Diary Status:*',
      ...rows.map((r) => `• *${r.classId} (${r.teacherName}):* ${diaryLine(r)}`), '',
      '⚠️ *Teachers with pending diary / homework are requested to submit their daily log immediately.*'];
  } else {
    const s = report.summary || {};
    lines = [...head('📊 *STAFF COMPLIANCE & DIARY AUDIT REPORT*'),
      `✅ *Full Compliance (Attendance + Diary):* ${s.compliant ?? 0}/${total}`, `⚠️ *Pending / Missing Submissions:* ${s.pending ?? 0}`, '',
      '📋 *Teacher-Wise Status:*',
      ...rows.flatMap((r) => [`• *${r.classId} (${r.teacherName}):*`, `  - Attendance: ${attLine(r)}`, `  - Diary: ${diaryLine(r)}`]), '',
      '⚠️ *Staff with pending submissions are requested to update their logs immediately.*'];
  }
  return lines.join('\n');
}

export const buildWhatsAppText = (report, scope) => shareComplianceWhatsApp(report, scope);

export const whatsAppUrl = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

/** Copies to clipboard (best effort) and opens WhatsApp share. Returns true if clipboard copy worked. */
export async function shareOnWhatsApp(report, scope) {
  const text = shareComplianceWhatsApp(report, scope);
  let copied = false;
  try { await navigator.clipboard.writeText(text); copied = true; } catch (e) { /* clipboard unavailable */ }
  window.open(whatsAppUrl(text), '_blank', 'noopener,noreferrer');
  return copied;
}

export { downloadCompliancePdf, downloadStaffCompliancePDF } from './compliancePdf';
